import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import { DetailPageFrame, DetailSection } from '@/components/football';
import Button from '@/components/ui/Button';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';
import MatchExportButton from '@/components/matches/MatchExportButton';
import AIChatPanel from '@/components/wyscout/AIChatPanel';
import MatchAdvancedStats from '@/components/wyscout/MatchAdvancedStats';
import MatchBuildUpPlays from '@/components/wyscout/MatchBuildUpPlays';
import MatchPlayerStats from '@/components/wyscout/MatchPlayerStats';
import { useMatchWorkspace } from '@/context/MatchWorkspaceContext';
import {
  MatchAnalysisPreset,
  MatchAnalysisSessionsEnvelope,
  matchKeys,
  matchesV2API,
  WyscoutBuildUpSection,
  WyscoutEventsEnvelope,
  WyscoutLineup,
  WyscoutMatchDetail,
  WyscoutPlayerStatsSection,
  WyscoutStatsSection,
} from '@/services/api';
import { getV2Error } from '@/services/api/v2';
import MatchIdentity from './detail/MatchIdentity';
import { MatchPageSection, MatchSectionNavigation, scrollToMatchSection } from './detail/MatchSectionNavigation';
import SectionQuery from './detail/SectionQuery';
import WyscoutEventsAnalysis from './detail/WyscoutEventsAnalysis';
import WyscoutLineups from './detail/WyscoutLineups';
import WyscoutMatchOverview, { getWyscoutTeamNames, readableFact, WyscoutMatchMetrics } from './detail/WyscoutMatchOverview';
import type { GoalFocus } from './detail/wyscout-events/types';
import { parseMatchTab, safeMatchesReturnTo, setMatchTab } from './MatchesPage/urlState';

const dataSections = [{ id: 'overview', label: 'Overview' }, { id: 'lineup', label: 'Lineup' }, { id: 'events', label: 'Events' }, { id: 'team-stats', label: 'Team stats' }, { id: 'player-stats', label: 'Player stats' }, { id: 'build-up', label: 'Build-up' }] as const;
const analysisItem = { id: 'analysis', label: 'Analysis' } as const;
type DataSection = typeof dataSections[number]['id'];
type Tab = DataSection | typeof analysisItem.id;
type LegacyGoalFocus = { minute: number; side: 'scored' | 'conceded' };
export const legacyGoalSide = (goal: GoalFocus, analysedTeamId: number): LegacyGoalFocus['side'] => (
  goal.teamId === analysedTeamId ? 'scored' : 'conceded'
);
const playerOptions = (lineup: WyscoutLineup): Array<{ playerId: number; name: string; position: string }> => [lineup.home, lineup.homeBench, lineup.away, lineup.awayBench].flatMap(players => players.map(player => ({
  playerId: Number(player.playerId),
  name: Number.isSafeInteger(Number(player.shirtNumber)) && Number(player.shirtNumber) >= 0 ? `#${Number(player.shirtNumber)}` : 'Unknown player',
  position: player.position || '',
})).filter(player => Number.isSafeInteger(player.playerId) && player.playerId > 0));
const lineupPlayerNames = (players: WyscoutPlayerStatsSection | undefined): ReadonlyMap<number, string> => {
  const names = new Map<number, string>();
  players?.forEach(player => {
    const name = (player.name || player.shortName || '').trim();
    if (Number.isSafeInteger(player.playerId) && player.playerId > 0 && name) names.set(player.playerId, name);
  });
  return names;
};

const WyscoutMatchPageContent: React.FC<{ raw: string }> = ({ raw }) => {
  const id = Number(raw);
  const workspaceId = useMatchWorkspace();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const initialChatSession = params.get('chatSession') || undefined;
  const fallbackTab = (initialChatSession ? 'analysis' : 'overview') as Tab;
  const tab = parseMatchTab(params, [...dataSections.map(value => value.id), analysisItem.id], fallbackTab);
  const sectionBaseId = `wyscout-match-${id}`;
  const [visibleSection, setVisibleSection] = useState<DataSection>(tab === 'analysis' ? 'overview' : tab);
  const pendingPopRestore = useRef<{ key: string; tab: DataSection; started: boolean } | null>(
    tab !== 'overview' && tab !== 'analysis' ? { key: location.key, tab, started: false } : null,
  );
  const lastLocationKey = useRef(location.key);
  const [, setRestoreComplete] = useState(0);
  if (lastLocationKey.current !== location.key) {
    lastLocationKey.current = location.key;
    if (navigationType === 'POP') {
      pendingPopRestore.current = tab === 'analysis' ? null : { key: location.key, tab, started: false };
    } else if (navigationType === 'PUSH') {
      pendingPopRestore.current = null;
    }
  }
  const pendingPopKey = pendingPopRestore.current?.key;
  const observeSections = pendingPopRestore.current === null;
  const analysisOpen = useRef(tab === 'analysis');
  analysisOpen.current = tab === 'analysis';
  const rawGoalMinute = params.get('goalMinute');
  const parsedGoalMinute = rawGoalMinute && /^\d+$/.test(rawGoalMinute) ? Number(rawGoalMinute) : null;
  const rawGoalSide = params.get('goalSide');
  const parsedGoalSide: LegacyGoalFocus['side'] | null = rawGoalSide === 'scored' || rawGoalSide === 'conceded' ? rawGoalSide : null;
  const goalFocus: LegacyGoalFocus | null = parsedGoalMinute !== null
    && Number.isSafeInteger(parsedGoalMinute)
    && parsedGoalMinute > 0
    && parsedGoalSide !== null
    ? { minute: parsedGoalMinute, side: parsedGoalSide }
    : null;
  const canonicalParams = setMatchTab(params, tab, fallbackTab);
  if (tab !== 'build-up' || !goalFocus) {
    canonicalParams.delete('goalMinute');
    canonicalParams.delete('goalSide');
  }
  if (tab !== 'analysis') canonicalParams.delete('chatSession');
  const canonicalSearch = canonicalParams.toString();
  useEffect(() => {
    if (params.toString() !== canonicalSearch) setParams(canonicalSearch, { replace: true });
  }, [canonicalSearch, params, setParams]);
  const returnTo = safeMatchesReturnTo(params.get('returnTo'));
  const [focusPlayerId, setFocusPlayerId] = useState<number | null>(null);
  const query = useQuery({ queryKey: matchKeys.wyscout(workspaceId, id), queryFn: () => matchesV2API.wyscout(workspaceId, id), enabled: Boolean(workspaceId && Number.isInteger(id) && id > 0) });
  const videoAvailabilityQuery = useQuery({ queryKey: matchKeys.videoAvailability(workspaceId, id), queryFn: () => matchesV2API.wyscoutVideoAvailability(workspaceId, id), enabled: Boolean(workspaceId && query.data), staleTime: 5 * 60 * 1000 });
  const statsQuery = useQuery({ queryKey: matchKeys.section(workspaceId, 'wyscout', id, 'stats'), queryFn: () => matchesV2API.wyscoutSection<WyscoutStatsSection>(workspaceId, id, 'stats'), enabled: Boolean(workspaceId && query.data) });
  const eventsQuery = useQuery({ queryKey: matchKeys.section(workspaceId, 'wyscout', id, 'events'), queryFn: () => matchesV2API.wyscoutSection<WyscoutEventsEnvelope>(workspaceId, id, 'events'), enabled: Boolean(workspaceId && query.data) });
  const playerStatsQuery = useQuery({ queryKey: matchKeys.section(workspaceId, 'wyscout', id, 'player-stats'), queryFn: () => matchesV2API.wyscoutSection<WyscoutPlayerStatsSection>(workspaceId, id, 'player-stats'), enabled: Boolean(workspaceId && query.data) });
  const buildUpQuery = useQuery({ queryKey: matchKeys.section(workspaceId, 'wyscout', id, 'build-up'), queryFn: () => matchesV2API.wyscoutSection<WyscoutBuildUpSection>(workspaceId, id, 'build-up'), enabled: Boolean(workspaceId && query.data) });
  const sync = useMutation({ mutationFn: () => matchesV2API.syncWyscoutMatch(workspaceId, id), onSuccess: async () => { await Promise.all([client.invalidateQueries({ queryKey: matchKeys.wyscout(workspaceId, id) }), client.invalidateQueries({ queryKey: matchKeys.root(workspaceId), predicate: value => value.queryKey[2] === 'browse' })]); }, onError: error => { if (getV2Error(error).code === 'forbidden') void query.refetch(); } });
  const listAnalysisSessions = useCallback(() => client.fetchQuery({ queryKey: matchKeys.analysisSessions(workspaceId, id), queryFn: ({ signal }) => matchesV2API.wyscoutChatSessions(workspaceId, id, signal) }).then(envelope => { client.setQueryData(matchKeys.wyscout(workspaceId, id), (current: WyscoutMatchDetail | undefined) => current ? { ...current, capabilities: envelope.capabilities } : current); return envelope.data; }), [client, id, workspaceId]);
  const getAnalysisSessionMessages = useCallback((sessionId: string) => client.fetchQuery({ queryKey: matchKeys.analysisSession(workspaceId, id, sessionId), queryFn: ({ signal }) => matchesV2API.wyscoutChatSession(workspaceId, id, sessionId, signal) }).then(envelope => { client.setQueryData(matchKeys.wyscout(workspaceId, id), (current: WyscoutMatchDetail | undefined) => current ? { ...current, capabilities: envelope.capabilities } : current); return envelope.data.messages; }), [client, id, workspaceId]);
  const deleteAnalysisSession = useCallback(async (sessionId: string) => { const envelope = await matchesV2API.deleteWyscoutChatSession(workspaceId, id, sessionId); client.removeQueries({ queryKey: matchKeys.analysisSession(workspaceId, id, sessionId) }); client.setQueryData<MatchAnalysisSessionsEnvelope>(matchKeys.analysisSessions(workspaceId, id), (current: MatchAnalysisSessionsEnvelope | undefined) => current ? { ...current, data: current.data.filter(session => session.id !== sessionId), capabilities: envelope.capabilities } : current); client.setQueryData(matchKeys.wyscout(workspaceId, id), (current: WyscoutMatchDetail | undefined) => current ? { ...current, capabilities: envelope.capabilities } : current); }, [client, id, workspaceId]);
  const updateUrl = useCallback((nextTab: Tab, replace: boolean, focus?: LegacyGoalFocus) => {
    const next = setMatchTab(params, nextTab, fallbackTab);
    if (nextTab !== 'analysis') next.delete('chatSession');
    if (focus) {
      next.set('goalMinute', String(focus.minute));
      next.set('goalSide', focus.side);
    } else if (nextTab !== 'build-up') {
      next.delete('goalMinute');
      next.delete('goalSide');
    }
    setParams(next, { replace });
  }, [fallbackTab, params, setParams]);
  const selectSection = useCallback((nextSection: DataSection) => {
    setVisibleSection(nextSection);
    updateUrl(nextSection, false);
  }, [updateUrl]);
  const openAnalysis = useCallback(() => {
    if (analysisOpen.current) return;
    analysisOpen.current = true;
    updateUrl('analysis', false);
  }, [updateUrl]);
  const returnToVisibleSection = useCallback(() => {
    analysisOpen.current = false;
    updateUrl(visibleSection, false);
  }, [updateUrl, visibleSection]);
  const observeSection = useCallback((nextSection: DataSection) => {
    setVisibleSection(nextSection);
    if (!analysisOpen.current) updateUrl(nextSection, true);
  }, [updateUrl]);
  const viewGoalBuildUp = useCallback((goal: GoalFocus) => {
    const side = legacyGoalSide(goal, query.data?.analysedTeamId || 0);
    const focus = Number.isSafeInteger(goal.minute) && goal.minute > 0 ? { minute: goal.minute, side } : undefined;
    setVisibleSection('build-up');
    updateUrl('build-up', false, focus);
    scrollToMatchSection(sectionBaseId, 'build-up');
  }, [query.data?.analysedTeamId, sectionBaseId, updateUrl]);
  useEffect(() => {
    const restore = pendingPopRestore.current;
    if (!query.data || !restore || restore.started) return undefined;
    restore.started = true;
    setVisibleSection(restore.tab);
    scrollToMatchSection(sectionBaseId, restore.tab);
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (pendingPopRestore.current !== restore) return;
        pendingPopRestore.current = null;
        setRestoreComplete(value => value + 1);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [pendingPopKey, query.data, sectionBaseId]);
  if (!workspaceId) return <p role="status">Select a workspace to view this match.</p>;
  if (!Number.isInteger(id) || id <= 0) return <p role="alert">Invalid match ID.</p>;
  if (query.isPending) return <DelayedLoading loading><LoadingStatus variant="panel" label="Loading Wyscout match" /></DelayedLoading>;
  if (query.isError || !query.data) { const error = getV2Error(query.error); return <div role="alert" className="space-y-3"><p>{error.code === 'provider_unavailable' ? 'Wyscout is currently unavailable for this workspace.' : error.message}</p><Button variant="outline" onClick={() => query.refetch()}>Retry</Button></div>; }
  const match = query.data;
  const videoAvailable = videoAvailabilityQuery.isSuccess && videoAvailabilityQuery.data.available;
  const videoAvailabilityLabel = videoAvailabilityQuery.isPending
    ? 'Checking video…'
    : videoAvailable
      ? `Video available${videoAvailabilityQuery.data.preferredQuality ? ` · ${videoAvailabilityQuery.data.preferredQuality.toUpperCase()}` : ''}`
      : 'No video available';
  const teamNames = getWyscoutTeamNames(match);
  const title = match.label || `${teamNames.home} v ${teamNames.away}`;
  const subtitle = [match.competition.name, match.season.name]
    .map(readableFact)
    .filter((value): value is string => value !== null)
    .join(', ') || undefined;
  const playerNames = lineupPlayerNames(playerStatsQuery.data);
  return <DetailPageFrame
    backHref={returnTo}
    backLabel="Back to matches"
    identity={<MatchIdentity source="wyscout" title={title} subtitle={subtitle} kickoffAt={match.kickoffAt} score={match.score} />}
    status={<div className="flex flex-wrap items-center gap-2"><p>{match.status}{match.venueName ? `, ${match.venueName}` : ''}</p><span role="status" className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">{videoAvailabilityLabel}</span></div>}
    metrics={statsQuery.data ? <WyscoutMatchMetrics match={match} stats={statsQuery.data} /> : undefined}
    actions={<>{match.capabilities.includes('wyscout.export') && <MatchExportButton download={signal => matchesV2API.exportWyscout(workspaceId, id, signal)} />}{match.capabilities.includes('wyscout.sync') && <Button loading={sync.isPending} loadingLabel="Synchronizing match" onClick={() => sync.mutate()}>Sync match</Button>}</>}
    tabs={<MatchSectionNavigation baseId={sectionBaseId} sections={dataSections} analysis={analysisItem} active={visibleSection} analysisOpen={tab === 'analysis'} observe={observeSections} onSelect={selectSection} onAnalysisSelect={openAnalysis} onVisibleSectionChange={observeSection} />}
    stickyTabs
  >
    {sync.isSuccess && <p role="status" className="rounded-lg bg-green-50 p-3 text-green-800 dark:bg-green-950 dark:text-green-200">Match synchronization completed.</p>}
    {sync.isError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-800 dark:bg-red-950 dark:text-red-200">{getV2Error(sync.error).message}</p>}
    {tab === 'analysis' && !match.capabilities.includes('wyscout.analysis.view') && <DetailSection title="Analysis unavailable"><p>You do not have permission to view Wyscout analysis.</p></DetailSection>}
    <MatchPageSection baseId={sectionBaseId} id="overview" label="Overview">
      <SectionQuery title="Match overview" query={statsQuery} unframed>{statsQuery.data && <WyscoutMatchOverview match={match} stats={statsQuery.data} events={eventsQuery.data?.events} showMetrics={false} />}</SectionQuery>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="lineup" label="Lineup">
      <WyscoutLineups lineup={match.lineup} homeTeamName={teamNames.home} awayTeamName={teamNames.away} kickoffAt={match.kickoffAt} playerNames={playerNames} />
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="events" label="Events">
      <SectionQuery title="Events" query={eventsQuery} unframed>{eventsQuery.data && <WyscoutEventsAnalysis events={eventsQuery.data.events} matchId={id} canWatchVideo={match.capabilities.includes('wyscout.video.view') && videoAvailable} requestEventVideo={eventId => matchesV2API.wyscoutEventVideo(workspaceId, id, eventId)} homeTeamId={match.homeTeam.id} awayTeamId={match.awayTeam.id} homeTeamName={teamNames.home} awayTeamName={teamNames.away} onViewGoalBuildUp={viewGoalBuildUp} />}</SectionQuery>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="team-stats" label="Team stats">
      <SectionQuery title="Team stats" query={statsQuery} unframed>{statsQuery.data && <MatchAdvancedStats matchId={id} homeTeamId={match.homeTeam.id} awayTeamId={match.awayTeam.id} homeTeamName={teamNames.home} awayTeamName={teamNames.away} statsData={statsQuery.data} />}</SectionQuery>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="player-stats" label="Player stats">
      <SectionQuery title="Player stats" query={playerStatsQuery} unframed>{playerStatsQuery.data && <MatchPlayerStats matchId={id} homeTeamId={match.homeTeam.id} awayTeamId={match.awayTeam.id} homeTeamName={teamNames.home} awayTeamName={teamNames.away} playersData={playerStatsQuery.data} />}</SectionQuery>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="build-up" label="Build-up" last>
      <SectionQuery title="Build-up" query={buildUpQuery} unframed>{buildUpQuery.data && <MatchBuildUpPlays matchId={id} homeTeamId={match.homeTeam.id} awayTeamId={match.awayTeam.id} homeTeamName={teamNames.home} awayTeamName={teamNames.away} analysisData={buildUpQuery.data} focusedGoalMinute={goalFocus?.minute} focusedGoalSide={goalFocus?.side} />}</SectionQuery>
    </MatchPageSection>
    {match.capabilities.includes('wyscout.analysis.view') && <AIChatPanel
        onSendLatestMessage={(message, preset, sessionId) => matchesV2API.sendWyscoutChat(workspaceId, id, message, preset as MatchAnalysisPreset | undefined, focusPlayerId ?? undefined, sessionId).then(envelope => { client.setQueryData(matchKeys.wyscout(workspaceId, id), (current: WyscoutMatchDetail | undefined) => current ? { ...current, capabilities: envelope.capabilities } : current); void client.invalidateQueries({ queryKey: matchKeys.analysisSessions(workspaceId, id) }); client.removeQueries({ queryKey: matchKeys.analysisSession(workspaceId, id, envelope.data.sessionId) }); return envelope.data; })}
        presets={[{ id: 'tactical', label: 'Tactical Analysis' }, { id: 'key_moments', label: 'Key Moments' }, { id: 'team_comparison', label: 'Team Comparison' }, { id: 'defensive_review', label: 'Defensive Review' }]}
        playerOptions={playerOptions(match.lineup)}
        onPlayerFocusChange={setFocusPlayerId}
        title="AI Match Analysis"
        entityType="match"
        entityId={String(id)}
        initialSessionId={initialChatSession}
        listSessions={listAnalysisSessions}
        getSessionMessages={getAnalysisSessionMessages}
        deleteSession={deleteAnalysisSession}
        maxMessageLength={4000}
        open={tab === 'analysis'}
        onOpenChange={open => open ? openAnalysis() : returnToVisibleSection()}
      />}
  </DetailPageFrame>;
};

const WyscoutMatchPage: React.FC = () => {
  const raw = useParams<{ matchId: string }>().matchId || '';
  return <WyscoutMatchPageContent key={raw} raw={raw} />;
};

export default WyscoutMatchPage;
